import { useEffect, useRef, useState } from 'react';
import { auditChecklist } from '../data';

export default function AccountAuditChecklist() {
  const [inView, setInView] = useState(false);
  const [checkedSteps, setCheckedSteps] = useState<Set<number>>(new Set());
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.08 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const toggleCheck = (step: number) => {
    setCheckedSteps((prev) => {
      const next = new Set(prev);
      if (next.has(step)) next.delete(step);
      else next.add(step);
      return next;
    });
  };

  const checkedCount = checkedSteps.size;
  const totalSteps = auditChecklist.length;
  const allChecked = checkedCount === totalSteps;

  return (
    <section ref={sectionRef} className="w-full bg-background-50 py-12 md:py-18">
      <div className="max-w-main mx-auto px-4 md:px-6">
        {/* Section header — same pattern as sibling sections */}
        <div className="max-w-[680px] mb-10 md:mb-14">
          <h2 className="text-[32px] md:text-[42px] font-[750] leading-[1.15] text-foreground-950 tracking-tight mb-4">
            Stop guessing. Start auditing.
          </h2>
          <p className="text-[15px] leading-relaxed text-foreground-600">
            Most traders never open their statements. The ones who do recover{' '}
            <span className="font-[800] text-foreground-950">$500–$2,000</span> in buried
            costs on the first pass. Work each line. Reconcile it when it clears.
          </p>
        </div>

        {/* Worksheet document */}
        <div
          className="bg-background-50 rounded-card border border-background-200 overflow-hidden"
          style={{
            boxShadow: '0 28px 70px -30px rgba(15,23,42,0.14)',
            opacity: inView ? 1 : 0,
            transform: inView ? 'translateY(0)' : 'translateY(26px)',
            transition: 'opacity 700ms ease-out, transform 700ms ease-out',
          }}
        >
          {/* ── Header band ── */}
          <div className="bg-background-100 px-6 md:px-10 pt-7 md:pt-8 pb-6 border-b border-dashed border-background-300 flex flex-wrap items-end justify-between gap-x-6 gap-y-4">
            <div>
              <div className="font-mono text-[10px] tracking-[0.22em] font-bold text-foreground-400 uppercase mb-2">
                Worksheet · Broker Cost Reconciliation
              </div>
              <div className="text-[18px] md:text-[21px] font-[750] text-foreground-950 tracking-tight leading-none">
                Statement Audit
              </div>
            </div>
            <div className="text-right">
              <div className="font-mono text-[10px] tracking-[0.18em] font-bold text-foreground-400 uppercase mb-2">
                Lines reconciled
              </div>
              <div className="flex items-baseline justify-end gap-1 tabular-nums leading-none">
                <span className="text-[26px] md:text-[30px] font-[800] text-foreground-950">
                  {String(checkedCount).padStart(2, '0')}
                </span>
                <span className="text-[17px] md:text-[19px] font-[700] text-foreground-300">
                  /{String(totalSteps).padStart(2, '0')}
                </span>
              </div>
            </div>
          </div>

          {/* ── Line items ── */}
          <div className="px-6 md:px-10 pt-8 md:pt-10 pb-2">
            {auditChecklist.map((item, idx) => {
              const isChecked = checkedSteps.has(item.step);
              const isLast = idx === auditChecklist.length - 1;

              return (
                <div
                  key={item.step}
                  className={`grid grid-cols-[46px_1fr] md:grid-cols-[72px_1fr] gap-x-4 md:gap-x-7 ${
                    isLast ? 'pb-8 md:pb-10' : 'pb-8 md:pb-9 mb-8 md:mb-9 border-b border-dashed border-background-200'
                  }`}
                  style={{
                    opacity: inView ? 1 : 0,
                    transform: inView ? 'translateY(0)' : 'translateY(16px)',
                    transition: 'opacity 520ms ease-out, transform 520ms ease-out',
                    transitionDelay: `${idx * 80 + 180}ms`,
                  }}
                >
                  {/* Index numeral column */}
                  <div className="text-right select-none">
                    <span
                      className={`block text-[34px] md:text-[46px] font-[800] tabular-nums tracking-tight leading-none transition-colors duration-300 ${
                        isChecked ? 'text-primary-400' : 'text-foreground-300'
                      }`}
                    >
                      {String(item.step).padStart(2, '0')}
                    </span>
                  </div>

                  {/* Content column */}
                  <div className="min-w-0">
                    {/* Title row with time estimate */}
                    <div className="flex items-baseline gap-3">
                      <h3
                        className={`text-[17px] md:text-[19px] font-bold leading-snug tracking-tight transition-colors duration-300 ${
                          isChecked ? 'text-foreground-500' : 'text-foreground-950'
                        }`}
                      >
                        {item.title}
                      </h3>
                      <span className="hidden sm:block flex-1 border-b border-dotted border-background-300 translate-y-[-3px] min-w-[16px]" />
                      <span className="font-mono text-[11px] text-foreground-400 shrink-0 tabular-nums whitespace-nowrap">
                        {item.timeEstimate.replace('~', '≈ ')}
                      </span>
                    </div>

                    {/* Description */}
                    <p className="text-[14px] md:text-[15px] text-foreground-600 leading-relaxed mt-2.5 max-w-[600px]">
                      {item.desc}
                    </p>

                    {/* Evidence note */}
                    <div className="mt-4 border-l-2 border-accent-400 bg-accent-50/50 pl-4 md:pl-5 pr-4 py-3.5 rounded-r-[6px] max-w-[600px]">
                      <div className="font-mono text-[10px] tracking-[0.2em] font-bold text-accent-700 uppercase mb-1.5">
                        Evidence to flag
                      </div>
                      <p className="text-[13px] md:text-[14px] text-foreground-700 leading-relaxed italic">
                        {item.whatToLook}
                      </p>
                    </div>

                    {/* Reconcile action */}
                    <button
                      onClick={() => toggleCheck(item.step)}
                      className={`mt-4 inline-flex items-center gap-2.5 text-[12px] font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                        isChecked
                          ? 'text-primary-600'
                          : 'text-foreground-500 hover:text-foreground-950'
                      }`}
                    >
                      <span
                        className={`w-[18px] h-[18px] rounded-[4px] border flex items-center justify-center transition-all duration-200 ${
                          isChecked
                            ? 'bg-primary-500 border-primary-500'
                            : 'border-foreground-300'
                        }`}
                      >
                        {isChecked && (
                          <i className="ri-check-line text-[11px] text-background-50 w-3 h-3 flex items-center justify-center" />
                        )}
                      </span>
                      {isChecked ? 'Line reconciled' : 'Mark this line reconciled'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* ── Footer ── */}
          {allChecked ? (
            <div className="border-t-2 border-foreground-950 bg-background-50 px-6 md:px-10 py-7 md:py-9">
              <div className="flex items-center gap-3 mb-5">
                <span className="font-mono text-[10px] tracking-[0.22em] font-bold text-primary-600 uppercase whitespace-nowrap">
                  Audit complete
                </span>
                <span className="flex-1 border-b border-dotted border-background-300 translate-y-[-1px]" />
                <span className="font-mono text-[10px] tracking-[0.22em] font-bold text-foreground-400 uppercase whitespace-nowrap tabular-nums">
                  {String(totalSteps).padStart(2, '0')} / {String(totalSteps).padStart(2, '0')} cleared
                </span>
              </div>

              <p className="text-[16px] md:text-[18px] italic text-foreground-800 leading-relaxed max-w-[640px]">
                &ldquo;The gap between what you thought you paid and what you actually paid
                &mdash; that is what your broker banked in silence.&rdquo;
              </p>

              <a
                href="/calculate-forex-trading-costs/"
                className="mt-6 inline-flex items-center gap-2 px-5 py-3 bg-foreground-950 text-background-50 text-[13px] font-bold rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                Run the numbers
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
              </a>
            </div>
          ) : (
            <div className="border-t border-dashed border-background-300 bg-background-100 px-6 md:px-10 py-5 flex items-center gap-3">
              <span className="text-[12px] text-foreground-500 italic leading-snug">
                Every unchecked line is a cost your broker hopes you never reconcile.
              </span>
              <span className="hidden sm:block flex-1 border-b border-dotted border-background-300 translate-y-[-1px]" />
              <span className="font-mono text-[10px] tracking-[0.2em] font-bold text-foreground-400 uppercase whitespace-nowrap shrink-0">
                Unaudited
              </span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}